import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import Select from "react-select";
import apiRequest from "../../lib/apiRequest";
import "./filter.scss";

const Filter = ({ selectedCategory, selectedSubCategory, subCategoryData, setFilteredProducts }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedOptions, setSelectedOptions] = useState({});
  const [isFilterVisible, setIsFilterVisible] = useState(false); // Управление видимостью фильтра

  const handleToggleFilter = () => setIsFilterVisible((prev) => !prev);
  
  // Управляем параметрами поиска с дебаунсом
  const [debouncedQuery, setDebouncedQuery] = useState({
    condition: searchParams.get("condition") || "",
    minPrice: searchParams.get("minPrice") || "",
    maxPrice: searchParams.get("maxPrice") || "",
    sortBy: searchParams.get("sortBy") || "date",
    sortOrder: searchParams.get("sortOrder") || "asc",
  });

  // Дебаунсинг параметров `searchParams`
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        Object.entries(debouncedQuery).forEach(([key, value]) => {
          newParams.set(key, value);
        });
        return newParams;
      });
    }, 500);

    return () => clearTimeout(handler);
  }, [debouncedQuery, setSearchParams]);

  // Обновление фильтрации с учётом новых значений
  const handleChange = useCallback((e) => {
    setDebouncedQuery((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  }, []);

  const handleOptionChange = useCallback((selectedValue, optionKey) => {
    setSelectedOptions((prevOptions) => {
      const newOptions = { ...prevOptions, [optionKey]: selectedValue };
      if (!selectedValue) delete newOptions[optionKey];
      return newOptions;
    });
  }, []);

  const options =
    selectedSubCategory?.length > 0
      ? selectedSubCategory[0].options ?? []
      : subCategoryData
          .find((category) => category.name.replace(/ /g, "_") === selectedCategory) // Приведение форматов
          ?.subcategories.map((sub) => sub.name) || [];

  // Обновление параметров поиска при изменении `selectedOptions`
  useEffect(() => {
    const newSearchParams = new URLSearchParams();

    Object.entries(selectedOptions).forEach(([key, value]) => {
      if (value) newSearchParams.set(key, value.value);
    });

    searchParams.forEach((value, key) => {
      if (!newSearchParams.has(key)) {
        newSearchParams.set(key, value);
      }
    });

    setSearchParams(newSearchParams);
  }, [selectedOptions, setSearchParams]);

  useEffect(() => {
    setSelectedOptions({});
  }, [searchParams.get("subcategory")]);

  // Фильтрация и сортировка товаров
  useEffect(() => {
    const condition = searchParams.get("condition") || "";
    const minPrice = Number(searchParams.get("minPrice")) || 0;
    const maxPrice = Number(searchParams.get("maxPrice")) || Infinity;

    // API-запрос и фильтрация
    apiRequest(`/posts?${searchParams.toString()}`)
      .then((postResponse) => {
        if (!Array.isArray(postResponse.data)) {
          console.error("⚠️ Ответ API не массив:", postResponse.data);
          setFilteredProducts([]);
          return;
        }

        const filteredByOptions = postResponse.data.filter((product) => {
          const isCategoryMatch = selectedCategory
            ? product.category.replace(/ /g, "_") === selectedCategory // Приведение категории
            : true;

          const isOptionsMatch = Object.entries(selectedOptions).every(([key, value]) => {
            if (!value || value.value === "") return true; // Пропускаем "Any"
            return product[key] === value.value;
          });

          return isCategoryMatch && isOptionsMatch;
        });
        setFilteredProducts(filteredByOptions);
      })
      .catch((error) => console.error("⚠️ Ошибка при запросе товаров:", error));
  }, [searchParams, selectedOptions, selectedCategory, setFilteredProducts]);

  const handleSubcategoryClick = useCallback((subcategory) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("subcategory", subcategory);
      return newParams;
    });
  }, []);

  return (
  <div className="filter">
    {/* Кнопка должна быть снаружи скрытого блока */}
     <button className="filter-toggle" onClick={handleToggleFilter}>
        <img src={isFilterVisible ? "/close.svg" : "/filter.svg"} alt="Filter" />
      </button>

    {isFilterVisible && (
      <div className="bottom">
        {selectedSubCategory ? (
          <div className="options">
            {Object.entries(options).map(([key, { name, values }]) => (
              <div className="option" key={key} style={{ marginBottom: "20px" }}>
                <label>{name}</label>
                <Select
                  options={[{ value: "", label: "All" }, ...values.map((value) => ({ value, label: value }))]}
                  onChange={(selectedValue) => handleOptionChange(selectedValue, key)}
                  placeholder="All"
                  value={selectedOptions[key] || null}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="subcategory-links">
            {options.map((sub, index) => (
              <a key={index} onClick={() => handleSubcategoryClick(sub)}>
                {sub.replaceAll("_", " ")}
              </a>
            ))}
          </div>
        )}

        {/* Фильтры */}
        <div className="item">
          <label htmlFor="minPrice">Min Price</label>
          <input type="number" name="minPrice" onChange={handleChange} value={debouncedQuery.minPrice} />
        </div>
        <div className="item">
          <label htmlFor="maxPrice">Max Price, ₦</label>
          <input type="number" name="maxPrice" onChange={handleChange} value={debouncedQuery.maxPrice} />
        </div>
        <div className="item">
          <label htmlFor="sortBy">Sort By</label>
          <select name="sortBy" onChange={handleChange} value={debouncedQuery.sortBy}>
            <option value="date">Date</option>
            <option value="price">Price</option>
          </select>
        </div>
        <div className="item">
          <label htmlFor="sortOrder">Order</label>
          <select name="sortOrder" onChange={handleChange} value={debouncedQuery.sortOrder}>
            <option value="desc">Descending</option>
            <option value="asc">Ascending</option>
          </select>
        </div>
        <div className="item">
          <label htmlFor="condition">Condition</label>
          <select name="condition" onChange={handleChange} value={debouncedQuery.condition}>
            <option value="">Any</option>
            <option value="new">New</option>
            <option value="used">Used</option>
          </select>
        </div>
      </div>
    )}
  </div>
);
};

export default Filter;
